import { IMrpUpdateSearchFilters } from "@/api_framework/api_modals/Inventory";
import ProductSectionSearch from "@/app/components/ui/Reusable/ProductSectionSearch";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useMrpUpdateSearchAPI } from "@/app/hooks/api_hooks/Inventory/MrpUpdate/useMrpUpdateSearchAPI";
import { useClinicTamsFilterAPI } from "@/app/hooks/api_hooks/Inventory/useClinicTamsFilterAPI";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useEffect, useState } from "react";
import { FieldError, useForm } from "react-hook-form";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";

export interface IMrpUpdateProps {
  type: string;
  clinic_tams: string;
  clinic_tams_id: string;
  productId: string;
  productName: string;
}

const types = [
  {
    id: "1",
    title: "TAMS",
  },
  {
    id: "2",
    title: "CLINIC",
  },
];

const MrpUpdate = () => {
  const { execute: fetchClinicList, clinicList } = useClinicTamsFilterAPI();
  const clinicsOrgItem = useSelectMenuReducer(clinicList, "display_name", "id");
  const { execute: mrpUpdateSearchPost, mrpUpdateSearch } = useMrpUpdateSearchAPI();
  const { setVisible: mrpUpdate, setModalData: setMrpUpdateModal } = useModalActions("MRP_UPDATE_MODAL");
  const [_shouldCallAPI, setShouldCallAPI] = useState(true);

  const formHook = useForm<IMrpUpdateProps>({
    mode: "onChange",
    defaultValues: { type: "", clinic_tams: "" },
  });

  useEffect(() => {
    const selectedType = formHook.watch("type");
    if (selectedType) {
      if (selectedType === "TAMS") {
        fetchClinicList("", "TAMS");
      } else if (selectedType === "CLINIC") {
        fetchClinicList("", "CLINIC");
      }
    }
  }, [formHook.watch("type")]);

  const onSubmit = async (data: IMrpUpdateProps) => {
    const constructedBody: IMrpUpdateSearchFilters = {
      clinic_id: data?.clinic_tams_id,
      vetic_variant_id: data?.productId,
    };
    mrpUpdateSearchPost(constructedBody);
  };

  const handleModalUpdate = () => {
    setShouldCallAPI(true);
    formHook.handleSubmit(onSubmit)();
  };

  const handleMrpUpdateClick = (item) => {
    setShouldCallAPI(false);
    setMrpUpdateModal({
      unique_row_id: item?.unique_row_id,
      product_name: item?.variant_name,
      lot_number: item?.lot_number,
      expiry_date: item?.expiry_date,
      current_mrp: item?.mrp,
      onUpdateSuccess: handleModalUpdate,
    });
    mrpUpdate();
  };

  return (
    <div className="bg-white px-4 p-4 rounded-md">
      <h2 className="flex justify-start items-center gap-x-3 border-gray-200 pb-3 border-b font-semibold text-sm">
        MRP Update
      </h2>
      <form className="mt-5" onSubmit={formHook.handleSubmit(onSubmit)}>
        <div className="flex gap-5">
          <div className="w-72 mt-5">
            <SearchableSelectMenu
              errorMessages={[{ type: "required", message: "Type is required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("type", item.title);
                }
              }}
              label="Type"
              fieldError={(formHook?.formState?.errors?.type as FieldError) ?? null}
              register={formHook.register("type", { required: true })}
              selectItems={types}
              placeholder="Select Type"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={types?.filter((oc) => oc.title === formHook.watch("type"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
            />
          </div>
          <div className="w-72 mt-5">
            <SearchableSelectMenu
              errorMessages={[{ type: "required", message: "Clinic/Tams is required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("clinic_tams", item.title);
                  formHook.setValue("clinic_tams_id", item.id);
                }
              }}
              label="Clinic/Tams"
              fieldError={(formHook?.formState?.errors?.clinic_tams as FieldError) ?? null}
              register={formHook.register("clinic_tams", { required: true })}
              selectItems={clinicsOrgItem}
              placeholder="Select Clinc/Tams"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={clinicsOrgItem?.filter((oc) => oc.title === formHook.watch("clinic_tams"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
            />
          </div>
          <div className="flex items-center gap-2 pb-3 w-[500px] mt-1.5">
            <ProductSectionSearch
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("productId", item.id);
                  formHook.setValue("productName", item.name);
                } else {
                  formHook.setValue("productId", null);
                  formHook.setValue("productName", null);
                }
              }}
              containerClassName="mt-2 w-full shadow-none"
              itemActiveClassName="bg-orange-500 text-white"
              inputContainerClassName="w-full"
              flexibleAdd={false}
              searchType="Lab"
            />
          </div>
          <div>
            <input
              type="submit"
              className="bg-black mt-[20px] px-6 py-2 border-transparent rounded-md text-sm text-white cursor-pointer"
              value={"Search"}
            />
          </div>
        </div>
      </form>
      {mrpUpdateSearch?.length > 0 && (
        <div className="w-full border border-gray-200 rounded-lg overflow-x-scroll mt-5">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100 text-left text-[10px] text-xs 2xl:text-sm font-medium">
                <th className="font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">S.NO</th>
                <th className="font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">Lot Number</th>
                <th className="font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">MRP</th>
                <th className="font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">Expiry Date</th>
                <th className="font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">In Hand Quantity</th>
                <th className="font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody className="text-[10px] text-xs 2xl:text-sm">
              {mrpUpdateSearch?.map((item, index) => (
                <tr key={index} className="odd:bg-white even:bg-gray-50">
                  <td className="sticky left-0 z-10 px-4 py-2">{index+1}</td>
                  <td className="px-4 py-2">{item?.lot_number}</td>
                  <td className="px-4 py-2">{item?.mrp}</td>
                  <td className="px-4 py-2">{item?.expiry_date}</td>
                  <td className="px-4 py-2">{item?.in_hand_quantity}</td>
                  <td className="px-4 py-2">
                    <button onClick={() => handleMrpUpdateClick(item)} className="text-red-500">
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MrpUpdate;
